import { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { useSite } from "@/lib/site";

export default function Gallery() {
  const { t, pickSuffix } = useI18n();
  const { gallery } = useSite();
  const [hover, setHover] = useState({});

  return (
    <section id="gallery" data-testid="gallery" className="carbon-surface py-20 sm:py-28 border-b border-zinc-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="tech-label mb-4 !text-brand-red">{t("gallery.label")}</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight chrome-text">
            {t("gallery.title")}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-zinc-400">{t("gallery.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {gallery.map((item, idx) => {
            const hasPair = Boolean(item.before_url) && Boolean(item.after_url);
            const single = item.after_url || item.before_url;
            const showAfter = hover[item.id] ?? false;
            const interactive = hasPair;

            return (
              <figure
                key={item.id}
                data-testid={`gallery-item-${idx}`}
                className={`relative group border border-zinc-800 bg-zinc-950 ${interactive ? "cursor-pointer" : ""}`}
                onMouseEnter={interactive ? () => setHover((s) => ({ ...s, [item.id]: true })) : undefined}
                onMouseLeave={interactive ? () => setHover((s) => ({ ...s, [item.id]: false })) : undefined}
                onClick={interactive ? () => setHover((s) => ({ ...s, [item.id]: !s[item.id] })) : undefined}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-zinc-900">
                  {hasPair ? (
                    <>
                      <img
                        src={item.before_url}
                        alt="before"
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${showAfter ? "opacity-0" : "opacity-100"}`}
                      />
                      <img
                        src={item.after_url}
                        alt="after"
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${showAfter ? "opacity-100" : "opacity-0"}`}
                      />
                      <div className={`absolute top-3 left-3 px-3 py-1.5 font-display font-black text-xs tracking-[0.2em] ${showAfter ? "bg-brand-red text-white" : "bg-black text-white border border-white/20"}`}>
                        {showAfter ? t("gallery.after") : t("gallery.before")}
                      </div>
                      <div className="absolute bottom-3 right-3 px-2.5 py-1 bg-black/80 backdrop-blur text-white text-[10px] font-bold tracking-wider uppercase border border-white/10">
                        {showAfter ? "↩ hover/click" : "hover/click →"}
                      </div>
                    </>
                  ) : single ? (
                    <img
                      src={single}
                      alt={pickSuffix(item, "title") || ""}
                      className="absolute inset-0 w-full h-full object-cover photo-enhance transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-zinc-600 text-sm">
                      No image
                    </div>
                  )}
                </div>
                {pickSuffix(item, "title") && (
                  <figcaption className="p-4 border-t border-zinc-800 font-display font-bold text-sm tracking-tight text-zinc-200">
                    {pickSuffix(item, "title")}
                  </figcaption>
                )}
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}
